/**
 * Order preview — slides in from the right, same idea as Live Orders.
 *
 * Structure: who / what / how much / where it is.
 *   header    customer, order id, store, new-vs-returning, payment
 *   note      the customer's delivery note, if they left one
 *   items     line items with quantities, then the money breakdown
 *   timeline  lifecycle, from placed through to delivered
 *
 * "Open full order" routes to the existing detail page, which keeps everything
 * else that lives there today.
 */

import React from 'react';
import { Drawer, Box, IconButton, Button, Chip, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useHUDTheme } from '../theme';
import { STATUS_META, statusKeyOf } from '../statusMeta';
import type { Order } from '../context/HUDContext';

/** Gateway id -> display name. */
const GATEWAY_LABELS: Record<string, string> = {
  ozow: 'Ozow',
  paygate: 'PayGate',
  snapscan: 'SnapScan',
  zapper: 'Zapper',
  payflex: 'Payflex',
  payjustnow: 'PayJustNow',
};

const num = (v: unknown) => Number(v ?? 0) || 0;

const money = (v: unknown) =>
  `R${num(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const stamp = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-ZA', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    })
    : null;

/**
 * Lifecycle, in the order it happens. The step the order is currently sitting in
 * reads "In progress"; everything ahead of it is "Pending".
 */
const TIMELINE: Array<{ key: keyof Order; label: string }> = [
  { key: 'createdAt', label: 'Order received' },
  { key: 'pickingStartedAt', label: 'Accepted · packing' },
  { key: 'packedAt', label: 'Packed · ready' },
  { key: 'dispatchedAt', label: 'Collected · en route' },
  { key: 'deliveredAt', label: 'Delivered' },
];

export default function OrderPreviewDrawer({ order, onClose }: {
  order: Order | null;
  onClose: () => void;
}) {
  const { hud } = useHUDTheme();
  const navigate = useNavigate();

  const meta = order ? STATUS_META[statusKeyOf(order)] : null;

  const customer = order
    ? `${order.user?.firstName ?? ''} ${order.user?.lastName ?? ''}`.trim() || 'Customer'
    : '';

  // totalOrders counts this one, so a first-timer sits at 1 (or is missing).
  const isReturning = (order?.user?.totalOrders ?? 1) > 1;

  const lines = order?.lines ?? [];
  const itemCount = lines.reduce((n: number, l: any) => n + (Number(l.quantity) || 1), 0);

  const gateway = order?.paymentMethod
    ? GATEWAY_LABELS[order.paymentMethod.toLowerCase()] ?? order.paymentMethod
    : null;

  // The last completed step is the one currently "in progress".
  const lastDoneIndex = order
    ? TIMELINE.reduce((last, step, i) => (order[step.key] ? i : last), -1)
    : -1;

  return (
    <Drawer
      anchor="right"
      open={!!order}
      onClose={onClose}
      PaperProps={{ sx: { width: 440, maxWidth: '94vw', bgcolor: hud.background, color: hud.textPrimary } }}
    >
      {order && meta && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* ── Header ─────────────────────────────────────────────────── */}
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${hud.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Box sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>{customer}</Box>
                <Box sx={{ color: hud.textMuted, fontFamily: 'monospace', fontSize: 13, mt: 0.25 }}>
                  Order #{order.number}
                </Box>
              </Box>
              <IconButton onClick={onClose} size="small" sx={{ color: hud.textSecondary }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <Chip
                size="small"
                label={meta.label}
                sx={{ bgcolor: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55`, fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={isReturning ? 'Return' : 'New'}
                sx={{
                  bgcolor: isReturning ? `${hud.accent}1f` : `${STATUS_META.delivered.color}1f`,
                  color: isReturning ? hud.accent : STATUS_META.delivered.color,
                  fontWeight: 700,
                }}
              />
              <Chip size="small" label={order.store?.name || order.storeId} sx={{ bgcolor: hud.surface, color: hud.textSecondary }} />
              {order.user?.phoneNumber && (
                <Chip size="small" label={order.user.phoneNumber} sx={{ bgcolor: hud.surface, color: hud.textSecondary }} />
              )}
              <Chip
                size="small"
                label={order.isPaid === false ? 'Unpaid' : 'Paid'}
                sx={{
                  bgcolor: order.isPaid === false ? `${hud.warning}20` : `${hud.success}20`,
                  color: order.isPaid === false ? hud.warning : hud.success,
                  fontWeight: 700,
                }}
              />
              {gateway && (
                <Chip size="small" label={gateway} sx={{ bgcolor: hud.surface, color: hud.textSecondary, fontWeight: 700 }} />
              )}
            </Box>
          </Box>

          {/* ── Body ───────────────────────────────────────────────────── */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
            {order.shippingNote && (
              <Box
                sx={{
                  mb: 2.5, p: 1.5, borderRadius: 1.5,
                  bgcolor: `${hud.warning}14`,
                  border: `1px solid ${hud.warning}44`,
                  color: hud.textPrimary,
                  fontSize: 14,
                }}
              >
                <Box sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: hud.warning, mb: 0.5 }}>
                  Delivery note
                </Box>
                “{order.shippingNote}”
              </Box>
            )}

            <SectionLabel hud={hud}>Items · {itemCount}</SectionLabel>
            <Box sx={{ bgcolor: hud.elevated, border: `1px solid ${hud.border}`, borderRadius: 2, p: 1.75, mb: 2 }}>
              {lines.length === 0 ? (
                <Box sx={{ color: hud.textMuted, fontSize: 14 }}>No line items on this order.</Box>
              ) : (
                lines.map((line: any, i: number) => {
                  const qty = Number(line.quantity) || 1;
                  const lineTotal = num(line.subtotalAmount ?? qty * num(line.price));
                  return (
                    <Box key={line.id ?? i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                      <Box sx={{ width: 28, fontWeight: 800, color: hud.textPrimary }}>{qty}</Box>
                      <Box sx={{ flex: 1, minWidth: 0, color: hud.textPrimary, fontSize: 14 }}>
                        {line.productName ?? 'Product'}
                      </Box>
                      <Box sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{money(lineTotal)}</Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Money. Subtotal, delivery, service fee and driver tip are always
                shown — they're on effectively every order, so a missing row is
                more confusing than a zero. Delivery reads "Free" when waived and
                the tip reads "None" when the customer didn't leave one. Promo is
                the exception: it only appears when a discount was actually used. */}
            <Box sx={{ bgcolor: hud.surface, borderRadius: 2, p: 1.75 }}>
              <Row hud={hud} label="Subtotal" value={money(order.totalCart)} />
              <Row hud={hud} label="Delivery fee" value={num(order.totalDeliveryFee) ? money(order.totalDeliveryFee) : 'Free'} />
              <Row hud={hud} label="Service fee" value={money(order.totalServiceFee)} />
              <Row hud={hud} label="Driver tip" value={num(order.totalDriverTip) ? money(order.totalDriverTip) : 'None'} />
              {num(order.totalPromo) > 0 && (
                <Row hud={hud} label="Promo" value={`− ${money(order.totalPromo)}`} />
              )}
              <Divider sx={{ my: 1, borderColor: hud.border }} />
              <Row hud={hud} label="Total" value={money(order.total)} bold />
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <SectionLabel hud={hud}>Timeline</SectionLabel>
              <Box sx={{ bgcolor: hud.elevated, border: `1px solid ${hud.border}`, borderRadius: 2, p: 1.75 }}>
                {TIMELINE.map((step, i) => {
                  const at = order[step.key] as string | undefined;
                  const done = !!at;
                  const current = i === lastDoneIndex;
                  return (
                    <Box key={String(step.key)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.6 }}>
                      <Box
                        sx={{
                          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                          bgcolor: done ? meta.color : 'transparent',
                          border: done ? 'none' : `2px solid ${hud.border}`,
                          boxShadow: current ? `0 0 0 4px ${meta.color}33` : 'none',
                        }}
                      />
                      <Box sx={{ flex: 1, color: done ? hud.textPrimary : hud.textMuted, fontWeight: done ? 700 : 500, fontSize: 14 }}>
                        {step.label}
                      </Box>
                      <Box sx={{ color: hud.textMuted, fontSize: 12 }}>
                        {current ? 'In progress' : done ? stamp(at) : 'Pending'}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* ── Footer ─────────────────────────────────────────────────── */}
          <Box sx={{ p: 2, borderTop: `1px solid ${hud.border}` }}>
            <Button
              fullWidth
              variant="contained"
              endIcon={<OpenInNewIcon />}
              onClick={() => {
                navigate(`/orders/${order.id}`);
                onClose();
              }}
              sx={{ bgcolor: hud.accent, fontWeight: 700, '&:hover': { bgcolor: hud.accent } }}
            >
              Open full order
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}

function SectionLabel({ children, hud }: { children: React.ReactNode; hud: any }) {
  return (
    <Box sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: hud.textMuted, mb: 0.75 }}>
      {children}
    </Box>
  );
}

function Row({ label, value, bold, hud }: { label: string; value: string; bold?: boolean; hud: any }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
      <Box sx={{ color: bold ? hud.textPrimary : hud.textMuted, fontWeight: bold ? 800 : 500 }}>{label}</Box>
      <Box sx={{ color: hud.textPrimary, fontWeight: bold ? 800 : 700 }}>{value}</Box>
    </Box>
  );
}
